/**
 * @file SuggestionInput component
 *
 * Will show suggested result as you type
 */

import { debounce } from 'lodash-es';
import Request from '../../utils/request';
import Data from '../../utils/dom/data';
import EventHandler from '../../utils/dom/event-handler';
import Manipulator from '../../utils/dom/manipulator';
import BaseComponent from '../BaseComponent';

const NAME = 'SuggestionInput';

const IS_LOCAL = false;
const DATA_KEY = 'lta.suggestioninput';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';
const VENUE_DATA_KEY = 'venueDetails';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;

const SELECTOR_DATA_TOGGLE = '[data-lta-suggestioninput]';

export default class SuggestionInput extends BaseComponent {
	constructor(element, config) {
		super(element);

		this.state = {
			typing: false,
			inputLength: 3,
			keepTypingLength: 3,
			eventName: 'venueSelect',
			dataKey: 'venueId',
			isSelected: false,
			additionalParams: config?.additionalParams || {},
			selectedResults: {}
		};
		this._dataFetchInProgress = false;

		this.suggestResults();
	}

	static get NAME() {
		return NAME;
	}

	// Getters
	static get DATA_KEY() {
		return DATA_KEY;
	}

	dispose() {
		super.dispose();
		// Remove event handlers, reset constructor public settings etc.
	}

	dataMapping(data) {
		return {
			firstLineText: data.venueName,
			secondLineText: `${data.venueAddress.street}, ${data.venueAddress.postcode} ${data.venueAddress.city}, ${data.venueAddress.country}`,
			dataKey: this.state.dataKey,
			dataValue: data.venueId
		};
	}

	filterDataMapping(data, event) {
		return data.venueId === event[this.state.dataKey];
	}

	suggestResults() {
		if (this._element !== null) {
			const settings = Manipulator.getDataAttributes(this._element) || {};
			if (!settings?.resultKey) {
				settings.resultKey = VENUE_DATA_KEY;
			}

			if (!this.state.dataMapping) {
				this.state.dataMapping = this.dataMapping.bind(this);
			}

			if (!this.state.filterDataMapping) {
				this.state.filterDataMapping = this.filterDataMapping.bind(this);
			}

			const closeButton = this._element.querySelector('.js-lta-text-input--close');
			const resultWrapper = this._element.querySelector('.lta-results-wrapper');
			const resultList = this._element.querySelector('.lta-suggest-results ul');
			const itemClass = 'lta-suggest-results--item';
			const input = closeButton.previousElementSibling;
			const inputWrapper = this._element.querySelector('#autosuggest-input');

			input.addEventListener('input', (event) => {
				this.state.typing =
					event.target.value.trim() !== '' && event.target.value.length >= this.state.keepTypingLength;

				if (this.state.typing) {
					closeButton.classList.remove('hide-btn');
					resultWrapper.classList.add('show-results');
				} else {
					closeButton.classList.add('hide-btn');
					resultWrapper.classList.remove('show-results');
				}
			});

			const createListItemMarkup = (index, markupData) => {
				const listElement = document.createElement('li');
				const button = document.createElement('div');
				const br = document.createElement('br');
				button.setAttribute('class', `${itemClass} u-pl-3 u-pt-1 u-pb-1`);
				button.setAttribute('type', 'button');
				button.setAttribute('role', 'option');
				button.setAttribute('id', `suggestion-${index + 1}`);
				if (markupData.dataKey) {
					button.dataset[markupData.dataKey] = markupData.dataValue;
				}

				const firstElementNode = document.createElement('span');
				const firstLineText = document.createTextNode(markupData.firstLineText);
				firstElementNode.append(firstLineText);
				button.append(firstElementNode);

				if (markupData.secondLineText) {
					const secondElementNode = document.createElement('span');
					const secondLineText = document.createTextNode(markupData.secondLineText);
					secondElementNode.append(secondLineText);
					button.append(br);
					button.append(secondElementNode);
				}
				listElement.append(button);
				return listElement;
			};

			const createItemMarkup = (item, index) => {
				const markupData = this.state?.dataMapping(item);
				return createListItemMarkup(index, markupData);
			};

			const filterPreviousData = (responseData) => {
				return settings.filterPrevious && responseData.length > 0
					? responseData.filter((venue) => this.state.selectedResults[venue.venueId] === undefined)
					: responseData;
			};
			const dataRequest = async () => {
				// execute only if characters length >= of specific number
				if (input.value.length < this.state.inputLength) {
					this._dataFetchInProgress = false;
					return;
				}

				if (this._element.dataset.endpoint) {
					try {
						const { endpoint, endpointkey } = this._element.dataset;
						const data = await Request.get(endpoint, { [endpointkey]: input.value.trim(), ...this.state?.additionalParams }, { local: IS_LOCAL });
						const responseData = data[settings.resultKey];
						const filterData = filterPreviousData(responseData);
						if (filterData.length > 0) {
							resultWrapper.classList.add('show-results');
							resultWrapper.classList.remove('has-no-results');
						} else {
							closeButton.classList.remove('hide-btn');
							resultWrapper.classList.add('show-results');
							resultWrapper.classList.add('has-no-results');
						}
						for (const [index, item] of filterData.entries()) {
							resultList.append(createItemMarkup(item, index));
						}
						inputWrapper.setAttribute('aria-expanded', 'true');
						const itemButtons = resultList.querySelectorAll(`.${itemClass}`);
						if (itemButtons.length > 0) {
							for (const button of itemButtons)
								button.addEventListener('click', (event) => {
									const resultFilterData = responseData.find((item) =>
										this.state?.filterDataMapping(item, event.currentTarget.dataset)
									);
									if (settings?.filterPrevious) {
										this.state.selectedResults[this.state.dataKey] = resultFilterData;
									}
									this.state.isSelected = true;
									const customEvent = new CustomEvent(this.state.eventName, {
										bubbles: true,
										detail: resultFilterData
									});
									this._element.dispatchEvent(customEvent);
									resultWrapper.classList.remove('show-results');
								});
						}
						this._dataFetchInProgress = false;
					} catch (error) {
						// eslint-disable-next-line no-console
						console.error(error);
					}
				}
			};

			input.addEventListener(
				'keydown',
				debounce((event) => {
					if (this.state.isSelected) {
						const key = event.keyCode || event.charCode;
						if (key == 8 || key == 46) {
							this.state.isSelected = false;
							input.value = '';
						}
					}
					resultList.textContent = '';
					if (!this._dataFetchInProgress) {
						this._dataFetchInProgress = true;
						dataRequest();
					}
				}),
				200
			);

			input.addEventListener(
				'focus',
				debounce(() => {
					resultList.textContent = '';
					if (!this._dataFetchInProgress) {
						this._dataFetchInProgress = true;
						dataRequest();
					}
				}),
				200
			);

			input.addEventListener('change', (e) => {
				if(input.value.trim() === '') {
					const customEvent = new CustomEvent(this.state.eventName, {
						bubbles: true,
						detail: ''
					});
					this._element.dispatchEvent(customEvent);
				}
			});

			closeButton.addEventListener('click', () => {
				input.value = '';
				resultList.textContent = '';
				closeButton.classList.add('hide-btn');
				resultWrapper.classList.remove('show-results');
				inputWrapper.setAttribute('aria-expanded', 'false');
				input.focus();
				const customEvent = new CustomEvent(this.state.eventName, {
					bubbles: true,
					detail: ''
				});
				this._element.dispatchEvent(customEvent);
				this.state.typing = false;
				this.state.isSelected = false;
			});

			document.addEventListener('click', (event) => {
				const isClickInside = this._element.contains(event.target);
				if (!isClickInside) {
					resultWrapper.classList.remove('show-results');
					inputWrapper.setAttribute('aria-expanded', 'false');
				}
			});
		}
	}

	clearSelectedResults() {
		this.state.selectedResults = {};
	}

	clearSelectedResult(id) {
		delete this.state.selectedResults[id];
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	// eslint-disable-next-line no-invalid-this
	Data.getData(this, DATA_KEY) || new SuggestionInput(this);
});
